import { useState, useMemo, useCallback } from 'react';
import { useCommonDataContext } from '@/common/hooks/commonDataContext';
import {
	MONTHS_OPTIONS,
	VIEW_TYPE_ENUM,
	VIEW_TYPE_OPTIONS,
} from '../constants/employeeKpiReport.constants';

export const useEmployeeKpiSearchBox = (onSearch) => {
	const {
		commonData: { queues = [], users = [] },
	} = useCommonDataContext();

	const [viewType, setViewType] = useState(VIEW_TYPE_OPTIONS[0]);
	const [selectedQueue, setSelectedQueue] = useState(null);
	const [selectedConsultants, setSelectedConsultants] = useState([]);
	const [selectedMonths, setSelectedMonths] = useState([]);
	const [dateRange, setDateRange] = useState({ from: '', to: '' });
	const [includeInactive, setIncludeInactive] = useState(false);

	const queueOptions = useMemo(() => {
		return queues
			.filter((q) => (includeInactive ? true : q.is_in_use))
			.map((q) => ({
				label: q.name,
				value: q._id,
				consultants: q.consultants,
			}));
	}, [queues, includeInactive]);

	const userOptions = useMemo(() => {
		let filtered = users;

		if (!includeInactive) {
			filtered = filtered.filter((u) => u.active);
		}

		if (selectedQueue) {
			filtered = filtered.filter((u) =>
				selectedQueue.consultants?.includes(u._id),
			);
		}

		return filtered.map((u) => ({ label: u.username, value: u._id }));
	}, [users, includeInactive, selectedQueue]);

	const handleQueueChange = (queue) => {
		setSelectedQueue(queue);

		const queueConsultants = users
			.filter((u) => queue?.consultants?.includes(u._id))
			.map((u) => ({ label: u.username, value: u._id }));

		setSelectedConsultants(queueConsultants);
	};

	const handleReset = useCallback(() => {
		setSelectedQueue(null);
		setSelectedConsultants([]);
	}, []);

	const handleSearch = (e) => {
		e?.preventDefault();

		const payload = {
			viewType: viewType.value,
			consultants: selectedConsultants.map((c) => c.value),
			includeInactive,
		};

		if (viewType.value === VIEW_TYPE_ENUM.DAILY) {
			payload.dateFrom = dateRange.from;
			payload.dateTo = dateRange.to;
		}

		if (viewType.value === VIEW_TYPE_ENUM.MONTHLY) {
			payload.months = selectedMonths.map((m) => m.value);
		}

		onSearch(payload);
	};

	return {
		state: {
			viewType,
			selectedQueue,
			selectedConsultants,
			selectedMonths,
			dateRange,
			includeInactive,
		},
		options: {
			queueOptions,
			userOptions,
			monthOptions: MONTHS_OPTIONS,
			viewTypeOptions: VIEW_TYPE_OPTIONS,
		},
		actions: {
			setViewType,
			setIncludeInactive,
			setSelectedConsultants,
			setSelectedMonths,
			setDateRange,
			handleQueueChange,
			handleReset,
			handleSearch,
		},
	};
};
